import React from "react";
import { filter, map, reduce } from "lodash";
import { Locale } from "@linkfluence/models/locale";
import CardComponent from "@radarly/components/panels/card-library/card";
import Cards from "@radarly/components/panels/card-library/cards";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { getColorFromTerm } from "@radarly/models/cards/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { PastedCard } from "@radarly/models/insight-pages/editor";
import { Section, Sections } from "./";

const formatMenuSection = (id: string, section: Section, locale: Locale) => ({
  id,
  title: section.title ? section.title : getTranslation(locale, id),
});

const getTranslation = (locale: Locale, id: string) => {
  return tr(locale, "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::" + id);
};

const flattenCards = (cards: Section["cards"], acc) =>
  reduce(
    cards || [],
    (acc, card, id) => {
      acc[id] = card;
      return acc;
    },
    acc
  );

const getVizCallBack = (cards: Section["cards"], id) => {
  if (cards[id] && cards[id].callback) {
    cards[id].callback.call(this);
  }
};

const getVizColor = (cards: Section["cards"], id: string, facet: FullFacet) => {
  return cards[id] && cards[id].facet
    ? getColorFromTerm(cards[id].viz, cards[id].facet, facet)
    : null;
};

const getVizOptions = (cards: Section["cards"], id) => {
  return cards[id] && cards[id].vizOptions ? cards[id].vizOptions : {};
};

const getCardsFromSection = (section: Section, id: string, locale: Locale) => {
  return {
    ...formatMenuSection(id, section, locale),
    cards: map(section.cards, (card, id) => ({
      viz: card.viz,
      id,
      title: card.title ? card.title : getTranslation(locale, id),
    })),
  };
};

const getMatchingCards = (
  cards: Section["cards"],
  search: string,
  locale: Locale
) =>
  filter(
    map(cards, (card, id) => ({
      ...card,
      id,
      title: card.title ? card.title : getTranslation(locale, id),
    })),
    (card) => card.title.toLowerCase().includes(search.toLowerCase())
  );

const getMatchingPastedCards = (pastedCards: PastedCard[], search: string) =>
  filter(pastedCards, (pastedCard) =>
    pastedCard?.title.toLowerCase().includes(search.toLowerCase())
  );

const getCardTranslation = (
  cards: Section["cards"],
  id: string,
  locale: Locale
) => {
  return cards[id] && cards[id].title
    ? cards[id].title
    : getTranslation(locale, id);
};

interface SharedPanelsCardEditionCardLibraryCardsProps {
  sections: Sections;
  currentSection: any;
  search: string;
  pastedCards: PastedCard[];
  onAddPastedCard: (pastedCard: PastedCard) => void;
}

export default function SharedPanelsCardEditionCardLibraryCards({
  sections,
  currentSection,
  search = "",
  pastedCards,
  onAddPastedCard,
}: SharedPanelsCardEditionCardLibraryCardsProps) {
  const locale = useCurrentUserLocale();
  const facets = useFacetsFromContext();
  const sectionsWithCards = map(sections, (section, id) => ({
    ...getCardsFromSection(section, id, locale),
    sections: map(section.sections, (section, id) => ({
      ...getCardsFromSection(section, id, locale),
    })),
  }));

  const cards = reduce(
    sections,
    (acc, section) => {
      flattenCards(section.cards, acc);
      reduce(
        section.sections,
        (acc, section) => {
          flattenCards(section.cards, acc);
          return acc;
        },
        acc
      );
      return acc;
    },
    {}
  );

  return (
    <Cards
      sections={search === "" ? sectionsWithCards : []}
      cards={search ? getMatchingCards(cards, search, locale) : []}
      renderViz={(viz, id) => (
        <CardComponent
          key={id}
          viz={viz}
          title={getCardTranslation(cards, id, locale)}
          onClick={() => getVizCallBack(cards, id)}
          color={getVizColor(cards, id, facets[cards[id]?.facet])}
          vizOptions={getVizOptions(cards, id)}
        />
      )}
      anchor={currentSection}
      locale={locale}
      pastedCards={
        search ? getMatchingPastedCards(pastedCards, search) : pastedCards
      }
      renderPastedViz={(pastedCard: PastedCard) => (
        <CardComponent
          viz={pastedCard.dataviz.name}
          title={pastedCard.title}
          onClick={() => onAddPastedCard(pastedCard)}
          color={pastedCard.color}
          vizOptions={pastedCard.vizOptions}
        />
      )}
    />
  );
}
